import React, { useState, useRef, useCallback, useEffect } from 'react';
import Stack from '@/components/Stack';
import Button from '@/components/Button';
import { Body, Small } from '@/components/Typography';
import useMediaQuery from '@/hooks/useMediaQuery';
import BREAKPOINTS from '@/styles/breakpoints';
import { SignatureField } from '@/types/documents';
import {
  getFieldConfig,
  formatFieldCounts,
  formatFieldSummaryByPage,
} from '@/utils/pdfFieldConfig';
import { loadPdfJs } from '@/utils/pdfLoader';
import PdfAlignmentGuides, { calculateSnap } from './PdfAlignmentGuides';
import PdfFieldSidebar from './PdfFieldSidebar';
import PdfPageThumbnails from './PdfPageThumbnails';

const HANDLE_SIZE = 8;

function getPointerPos(
  e: React.MouseEvent | React.TouchEvent,
  rect: DOMRect,
): { x: number; y: number } {
  if ('touches' in e) {
    const touch = e.touches[0] || (e as React.TouchEvent).changedTouches[0];
    return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
  }
  return {
    x: (e as React.MouseEvent).clientX - rect.left,
    y: (e as React.MouseEvent).clientY - rect.top,
  };
}

interface InternalField extends SignatureField {
  _id: number;
}

interface PdfSignaturePlacerProps {
  pdfUrl: string;
  initialFields?: SignatureField[];
  onFieldsChange: (fields: SignatureField[]) => void;
}

type ResizeHandle = 'tl' | 'tr' | 'bl' | 'br';

let nextFieldId = 1;

const SURFACE_BORDER = '#d6deeb';
const SURFACE_BACKGROUND = '#f8fafc';
const TOOLBAR_BACKGROUND = 'rgba(255, 255, 255, 0.94)';

const groupShellStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  minWidth: 'fit-content',
  padding: '14px 16px',
  border: `1px solid ${SURFACE_BORDER}`,
  borderRadius: '14px',
  background: '#fff',
  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)',
};

const groupActionsStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flexWrap: 'wrap',
};

function stripInternalId(field: InternalField): SignatureField {
  const { _id, ...rest } = field;
  return rest;
}

const ToolbarGroup = ({
  label,
  children,
}: React.PropsWithChildren<{ label: string }>) => {
  return (
    <div style={groupShellStyle}>
      <Small
        style={{
          color: '#64748b',
          fontSize: '11px',
          fontWeight: 700,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </Small>
      <div style={groupActionsStyle}>{children}</div>
    </div>
  );
};

const PdfSignaturePlacer: React.FC<PdfSignaturePlacerProps> = ({
  pdfUrl,
  initialFields,
  onFieldsChange,
}) => {
  const isDesktop = useMediaQuery(BREAKPOINTS.desktop);
  const isTouchable = useMediaQuery(BREAKPOINTS.touchable);

  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageWidth, setPageWidth] = useState<number>(612);
  const [pageHeight, setPageHeight] = useState<number>(792);
  const [scale, setScale] = useState<number>(1);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(true);

  const [fields, setFields] = useState<InternalField[]>(() =>
    (initialFields || []).map((f) => ({ ...f, _id: nextFieldId++ })),
  );
  const [selectedFieldId, setSelectedFieldId] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizing, setResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState<ResizeHandle | null>(null);
  const [resizeAnchor, setResizeAnchor] = useState({ x: 0, y: 0 });
  const [pageInput, setPageInput] = useState<string>('');

  const [history, setHistory] = useState<InternalField[][]>([]);
  const [historyIndex, setHistoryIndex] = useState<number>(-1);
  const isUndoRedoRef = useRef(false);

  const [activeGuides, setActiveGuides] = useState<{
    x: number | null;
    y: number | null;
  }>({ x: null, y: null });

  const [userScale, setUserScale] = useState<number | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [thumbnailsCollapsed, setThumbnailsCollapsed] = useState(false);
  const [viewportWidth, setViewportWidth] = useState<number>(() =>
    typeof window === 'undefined' ? 0 : window.innerWidth,
  );
  const autoScaleRef = useRef<number>(1);

  const containerRef = useRef<HTMLDivElement>(null);
  const viewerPaneRef = useRef<HTMLDivElement>(null);
  const pageContainerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pdfDocRef = useRef<any>(null);

  // Notify parent of field changes
  const emitFields = useCallback(
    (updatedFields: InternalField[]) => {
      onFieldsChange(updatedFields.map(stripInternalId));
    },
    [onFieldsChange],
  );

  const pushHistory = useCallback(
    (snapshot: InternalField[]) => {
      if (isUndoRedoRef.current) return;
      setHistory((prev) => {
        const truncated = prev.slice(0, historyIndex + 1);
        const next = [...truncated, snapshot.map((f) => ({ ...f }))];
        if (next.length > 50) next.shift();
        return next;
      });
      setHistoryIndex((prev) => Math.min(prev + 1, 49));
    },
    [historyIndex],
  );

  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setThumbnailsCollapsed(!isDesktop);
    setSidebarCollapsed(!isDesktop);
  }, [isDesktop]);

  const undo = useCallback(() => {
    if (historyIndex <= 0) return;
    const newIndex = historyIndex - 1;
    isUndoRedoRef.current = true;
    const snapshot = history[newIndex].map((f) => ({ ...f }));
    setFields(snapshot);
    setHistoryIndex(newIndex);
    setSelectedFieldId(null);
    onFieldsChange(snapshot.map(stripInternalId));
    isUndoRedoRef.current = false;
  }, [history, historyIndex, onFieldsChange]);

  const redo = useCallback(() => {
    if (historyIndex >= history.length - 1) return;
    const newIndex = historyIndex + 1;
    isUndoRedoRef.current = true;
    const snapshot = history[newIndex].map((f) => ({ ...f }));
    setFields(snapshot);
    setHistoryIndex(newIndex);
    setSelectedFieldId(null);
    onFieldsChange(snapshot.map(stripInternalId));
    isUndoRedoRef.current = false;
  }, [history, historyIndex, onFieldsChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      const isMeta = e.metaKey || e.ctrlKey;

      if (isMeta && e.key === 'z' && e.shiftKey) {
        e.preventDefault();
        redo();
        return;
      }
      if (isMeta && e.key === 'z') {
        e.preventDefault();
        undo();
        return;
      }
      if (isMeta && e.key === 'd') {
        e.preventDefault();
        if (selectedFieldId !== null) duplicateField(selectedFieldId);
        return;
      }
      if (e.key === 's' && !isMeta) {
        addField('signature');
        return;
      }
      if (e.key === 'd' && !isMeta) {
        addField('date');
        return;
      }
      if (e.key === 'n' && !isMeta) {
        addField('name');
        return;
      }
      if (
        (e.key === 'Delete' || e.key === 'Backspace') &&
        selectedFieldId !== null
      ) {
        e.preventDefault();
        deleteField(selectedFieldId);
        return;
      }
      if (e.key === 'Escape') {
        setSelectedFieldId(null);
        return;
      }
    },
    [addField, deleteField, duplicateField, undo, redo, selectedFieldId],
  );

  // PDF to screen coordinate conversion
  const pdfToScreen = useCallback(
    (px: number, py: number, pw: number, ph: number) => ({
      screenX: px * scale,
      screenY: (pageHeight - py - ph) * scale,
      screenW: pw * scale,
      screenH: ph * scale,
    }),
    [scale, pageHeight],
  );

  // Screen to PDF coordinate conversion
  const screenToPdf = useCallback(
    (sx: number, sy: number, sw: number, sh: number) => ({
      pdfX: sx / scale,
      pdfY: pageHeight - (sy + sh) / scale,
      pdfW: sw / scale,
      pdfH: sh / scale,
    }),
    [scale, pageHeight],
  );

  // Load PDF document
  useEffect(() => {
    let cancelled = false;

    async function loadPdf() {
      setPdfLoading(true);
      setPdfError(null);
      try {
        const lib = await loadPdfJs();
        const doc = await lib.getDocument(pdfUrl).promise;
        if (cancelled) return;
        pdfDocRef.current = doc;
        setNumPages(doc.numPages);
      } catch (err: any) {
        if (!cancelled) {
          setPdfError(`Failed to load PDF: ${err.message || err}`);
        }
      } finally {
        if (!cancelled) setPdfLoading(false);
      }
    }

    loadPdf();
    return () => {
      cancelled = true;
    };
  }, [pdfUrl]);

  // Render current page to canvas
  useEffect(() => {
    const doc = pdfDocRef.current;
    if (!doc || currentPage < 1 || currentPage > numPages) return;

    let cancelled = false;

    async function renderPage() {
      try {
        const page = await doc.getPage(currentPage);
        if (cancelled) return;

        const viewport = page.getViewport({ scale: 1 });
        setPageWidth(viewport.width);
        setPageHeight(viewport.height);

        let newScale = 1;
        const viewerWidth =
          viewerPaneRef.current?.clientWidth ||
          containerRef.current?.clientWidth;
        if (viewerWidth) {
          const containerWidth = Math.max(
            viewerWidth - (isTouchable ? 32 : 64),
            240,
          );
          newScale = Math.min(containerWidth / viewport.width, 1.5);
        }
        autoScaleRef.current = newScale;
        const effectiveScale = userScale !== null ? userScale : newScale;
        setScale(effectiveScale);

        const scaledViewport = page.getViewport({ scale: effectiveScale });
        const canvas = canvasRef.current;
        if (!canvas || cancelled) return;

        canvas.width = scaledViewport.width;
        canvas.height = scaledViewport.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        await page.render({ canvasContext: ctx, viewport: scaledViewport })
          .promise;
      } catch (err: any) {
        if (!cancelled) {
          setPdfError(`Failed to render page: ${err.message || err}`);
        }
      }
    }

    renderPage();
    return () => {
      cancelled = true;
    };
  }, [numPages, currentPage, userScale, viewportWidth, isTouchable]);

  useEffect(() => {
    if (fields.length > 0 && history.length === 0) {
      setHistory([fields.map((f) => ({ ...f }))]);
      setHistoryIndex(0);
    }
  }, []);

  const fieldsOnCurrentPage = fields.filter((f) => f.page === currentPage);

  const fieldsPerPage: Record<number, number> = {};
  for (const f of fields) {
    fieldsPerPage[f.page] = (fieldsPerPage[f.page] || 0) + 1;
  }

  const addField = useCallback(
    (fieldType: 'signature' | 'date' | 'name') => {
      const config = getFieldConfig(fieldType);
      const w = config.defaultWidth;
      const h = config.defaultHeight;
      const x = (pageWidth - w) / 2;
      const y = (pageHeight - h) / 2;
      const maxSortOrder =
        fields.length > 0 ? Math.max(...fields.map((f) => f.sort_order)) : 0;

      const newField: InternalField = {
        _id: nextFieldId++,
        field_type: fieldType,
        page: currentPage,
        x,
        y,
        width: w,
        height: h,
        sort_order: maxSortOrder + 1,
      };

      const updated = [...fields, newField];
      setFields(updated);
      setSelectedFieldId(newField._id);
      emitFields(updated);
      pushHistory(updated);
    },
    [fields, currentPage, pageWidth, pageHeight, emitFields, pushHistory],
  );

  const deleteField = useCallback(
    (fieldId: number) => {
      const updated = fields.filter((f) => f._id !== fieldId);
      setFields(updated);
      if (selectedFieldId === fieldId) {
        setSelectedFieldId(null);
      }
      emitFields(updated);
      pushHistory(updated);
    },
    [fields, selectedFieldId, emitFields, pushHistory],
  );

  const duplicateField = useCallback(
    (fieldId: number) => {
      const source = fields.find((f) => f._id === fieldId);
      if (!source) return;
      const offset = 15;
      const maxSortOrder = Math.max(...fields.map((f) => f.sort_order), 0);
      const newX = Math.min(source.x + offset, pageWidth - source.width);
      const newY = Math.max(source.y - offset, 0);
      const newField: InternalField = {
        ...source,
        _id: nextFieldId++,
        x: newX,
        y: newY,
        sort_order: maxSortOrder + 1,
      };
      const updated = [...fields, newField];
      setFields(updated);
      setSelectedFieldId(newField._id);
      emitFields(updated);
      pushHistory(updated);
    },
    [fields, pageWidth, emitFields, pushHistory],
  );

  const zoomIn = useCallback(() => {
    setUserScale((prev) => Math.min((prev ?? scale) + 0.25, 2.0));
  }, [scale]);

  const zoomOut = useCallback(() => {
    setUserScale((prev) => Math.max((prev ?? scale) - 0.25, 0.5));
  }, [scale]);

  const zoomFit = useCallback(() => {
    setUserScale(null);
  }, []);

  const updateField = useCallback(
    (fieldId: number, updates: Partial<InternalField>) => {
      const updated = fields.map((f) =>
        f._id === fieldId ? { ...f, ...updates } : f,
      );
      setFields(updated);
      emitFields(updated);
    },
    [fields, emitFields],
  );

  const getFieldAtScreenPos = useCallback(
    (mouseX: number, mouseY: number): InternalField | null => {
      // Check in reverse order so topmost fields are selected first
      for (let i = fieldsOnCurrentPage.length - 1; i >= 0; i--) {
        const f = fieldsOnCurrentPage[i];
        const { screenX, screenY, screenW, screenH } = pdfToScreen(
          f.x,
          f.y,
          f.width,
          f.height,
        );
        if (
          mouseX >= screenX &&
          mouseX <= screenX + screenW &&
          mouseY >= screenY &&
          mouseY <= screenY + screenH
        ) {
          return f;
        }
      }
      return null;
    },
    [fieldsOnCurrentPage, pdfToScreen],
  );

  const getResizeHandleAtPos = useCallback(
    (
      mouseX: number,
      mouseY: number,
      field: InternalField,
    ): ResizeHandle | null => {
      const { screenX, screenY, screenW, screenH } = pdfToScreen(
        field.x,
        field.y,
        field.width,
        field.height,
      );
      const handles: { handle: ResizeHandle; cx: number; cy: number }[] = [
        { handle: 'tl', cx: screenX, cy: screenY },
        { handle: 'tr', cx: screenX + screenW, cy: screenY },
        { handle: 'bl', cx: screenX, cy: screenY + screenH },
        { handle: 'br', cx: screenX + screenW, cy: screenY + screenH },
      ];
      for (const { handle, cx, cy } of handles) {
        if (
          mouseX >= cx - HANDLE_SIZE &&
          mouseX <= cx + HANDLE_SIZE &&
          mouseY >= cy - HANDLE_SIZE &&
          mouseY <= cy + HANDLE_SIZE
        ) {
          return handle;
        }
      }
      return null;
    },
    [pdfToScreen],
  );

  const handlePointerDown = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      const rect = pageContainerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const { x: mouseX, y: mouseY } = getPointerPos(e, rect);

      // Check if clicking a resize handle on selected field
      if (selectedFieldId !== null) {
        const selectedField = fields.find((f) => f._id === selectedFieldId);
        if (selectedField && selectedField.page === currentPage) {
          const handle = getResizeHandleAtPos(mouseX, mouseY, selectedField);
          if (handle) {
            const { screenX, screenY, screenW, screenH } = pdfToScreen(
              selectedField.x,
              selectedField.y,
              selectedField.width,
              selectedField.height,
            );
            // The anchor is the opposite corner
            const anchorX =
              handle === 'tl' || handle === 'bl' ? screenX + screenW : screenX;
            const anchorY =
              handle === 'tl' || handle === 'tr' ? screenY + screenH : screenY;
            setResizing(true);
            setResizeHandle(handle);
            setResizeAnchor({ x: anchorX, y: anchorY });
            return;
          }
        }
      }

      // Check if clicking on a field
      const clickedField = getFieldAtScreenPos(mouseX, mouseY);
      if (clickedField) {
        setSelectedFieldId(clickedField._id);
        const { screenX, screenY } = pdfToScreen(
          clickedField.x,
          clickedField.y,
          clickedField.width,
          clickedField.height,
        );
        setDragging(true);
        setDragOffset({ x: mouseX - screenX, y: mouseY - screenY });
        return;
      }

      // Clicked on empty area — deselect
      setSelectedFieldId(null);
    },
    [
      fields,
      selectedFieldId,
      currentPage,
      pdfToScreen,
      getFieldAtScreenPos,
      getResizeHandleAtPos,
    ],
  );

  const handlePointerMove = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!dragging && !resizing) return;
      if ('touches' in e) e.preventDefault(); // prevent scroll while dragging
      const rect = pageContainerRef.current?.getBoundingClientRect();
      if (!rect || selectedFieldId === null) return;

      const { x: mouseX, y: mouseY } = getPointerPos(e, rect);
      const selectedField = fields.find((f) => f._id === selectedFieldId);
      if (!selectedField) return;

      const canvasW = pageWidth * scale;
      const canvasH = pageHeight * scale;

      if (dragging) {
        const { screenW, screenH } = pdfToScreen(
          selectedField.x,
          selectedField.y,
          selectedField.width,
          selectedField.height,
        );
        const newScreenX = Math.max(
          0,
          Math.min(mouseX - dragOffset.x, canvasW - screenW),
        );
        const newScreenY = Math.max(
          0,
          Math.min(mouseY - dragOffset.y, canvasH - screenH),
        );

        const draggedRect = {
          screenX: newScreenX,
          screenY: newScreenY,
          screenW,
          screenH,
        };
        const otherRects = fieldsOnCurrentPage
          .filter((f) => f._id !== selectedFieldId)
          .map((f) => pdfToScreen(f.x, f.y, f.width, f.height))
          .map(({ screenX, screenY, screenW: sw, screenH: sh }) => ({
            screenX,
            screenY,
            screenW: sw,
            screenH: sh,
          }));

        const { snap, guides } = calculateSnap(draggedRect, otherRects);
        setActiveGuides(guides);

        const finalX = snap.snapX !== null ? snap.snapX : newScreenX;
        const finalY = snap.snapY !== null ? snap.snapY : newScreenY;

        const converted = screenToPdf(finalX, finalY, screenW, screenH);
        updateField(selectedFieldId, {
          x: converted.pdfX,
          y: converted.pdfY,
        });
      }

      if (resizing && resizeHandle) {
        const fConfig = getFieldConfig(selectedField.field_type);
        const minW = fConfig.minScreenW;
        const minH = fConfig.minScreenH;

        // The moving corner follows the mouse, anchor stays fixed
        let left = Math.min(mouseX, resizeAnchor.x);
        let top = Math.min(mouseY, resizeAnchor.y);
        let right = Math.max(mouseX, resizeAnchor.x);
        let bottom = Math.max(mouseY, resizeAnchor.y);

        let w = right - left;
        let h = bottom - top;

        // Enforce minimums
        if (w < minW) {
          w = minW;
          if (mouseX < resizeAnchor.x) {
            left = resizeAnchor.x - minW;
          } else {
            right = resizeAnchor.x + minW;
            left = resizeAnchor.x;
          }
        }
        if (h < minH) {
          h = minH;
          if (mouseY < resizeAnchor.y) {
            top = resizeAnchor.y - minH;
          } else {
            bottom = resizeAnchor.y + minH;
            top = resizeAnchor.y;
          }
        }

        // Constrain to page bounds
        left = Math.max(0, left);
        top = Math.max(0, top);
        right = Math.min(canvasW, left + w);
        bottom = Math.min(canvasH, top + h);
        w = right - left;
        h = bottom - top;

        const converted = screenToPdf(left, top, w, h);
        updateField(selectedFieldId, {
          x: converted.pdfX,
          y: converted.pdfY,
          width: converted.pdfW,
          height: converted.pdfH,
        });
      }
    },
    [
      dragging,
      resizing,
      resizeHandle,
      resizeAnchor,
      selectedFieldId,
      fields,
      dragOffset,
      pageWidth,
      pageHeight,
      scale,
      pdfToScreen,
      screenToPdf,
      updateField,
    ],
  );

  const handlePointerUp = useCallback(() => {
    if (dragging || resizing) {
      pushHistory(fields);
    }
    setDragging(false);
    setResizing(false);
    setResizeHandle(null);
    setActiveGuides({ x: null, y: null });
  }, [dragging, resizing, fields, pushHistory]);

  const handleSidebarSelectField = useCallback(
    (fieldId: number, page: number) => {
      setSelectedFieldId(fieldId);
      if (page !== currentPage) setCurrentPage(page);
    },
    [currentPage],
  );

  const handleSidebarReorder = useCallback(
    (reorderedIds: number[]) => {
      const updated = fields.map((f) => ({
        ...f,
        sort_order: reorderedIds.indexOf(f._id),
      }));
      setFields(updated);
      emitFields(updated);
      pushHistory(updated);
    },
    [fields, emitFields, pushHistory],
  );

  const summaryText = formatFieldSummaryByPage(fields);
  const pagesWithFields = Object.keys(fieldsPerPage).length;
  const hasSignatureField = fields.some(
    (field) => field.field_type === 'signature',
  );
  const placementStatusLabel = hasSignatureField
    ? 'Ready'
    : 'Needs signature field';
  const placementStatusColor = hasSignatureField ? '#166534' : '#9a3412';
  const placementStatusBackground = hasSignatureField ? '#dcfce7' : '#ffedd5';
  const fieldInventoryText =
    fields.length === 0
      ? 'No fields placed yet'
      : `${fields.length} field${fields.length === 1 ? '' : 's'} across ${pagesWithFields} page${pagesWithFields === 1 ? '' : 's'}`;
  const fieldTypeText =
    fields.length === 0
      ? 'Add signature, date, and name fields to start placing inputs on the document.'
      : formatFieldCounts(fields);
  const pageStatusText =
    numPages > 0 ? `Page ${currentPage} of ${numPages}` : 'Loading pages';

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      style={{ outline: 'none' }}
      onKeyDown={handleKeyDown}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          border: `1px solid ${SURFACE_BORDER}`,
          borderRadius: '24px',
          padding: isTouchable ? '16px' : '20px',
          background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
          boxShadow: '0 18px 48px rgba(15, 23, 42, 0.08)',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            gap: '16px',
            alignItems: 'flex-start',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <Small
              style={{
                color: '#64748b',
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}
            >
              PDF field editor
            </Small>
            <Body
              style={{
                color: '#0f172a',
                fontSize: '28px',
                fontWeight: 700,
                lineHeight: 1.1,
              }}
            >
              Signature Fields
            </Body>
            <Small style={{ color: '#475569', maxWidth: '720px' }}>
              Place signature, date, and name fields directly on the PDF, then
              drag or resize them inside the workspace.
            </Small>
          </div>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
              gap: '12px',
            }}
          >
            <div
              style={{
                minWidth: '170px',
                padding: '12px 14px',
                borderRadius: '16px',
                border: `1px solid ${SURFACE_BORDER}`,
                background: '#fff',
              }}
            >
              <Small style={{ color: '#64748b', display: 'block' }}>
                Placement status
              </Small>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  marginTop: '8px',
                  padding: '6px 10px',
                  borderRadius: '999px',
                  background: placementStatusBackground,
                  color: placementStatusColor,
                  fontSize: '12px',
                  fontWeight: 700,
                }}
              >
                {placementStatusLabel}
              </span>
            </div>
            <div
              style={{
                minWidth: '190px',
                padding: '12px 14px',
                borderRadius: '16px',
                border: `1px solid ${SURFACE_BORDER}`,
                background: '#fff',
              }}
            >
              <Small style={{ color: '#64748b', display: 'block' }}>
                Field summary
              </Small>
              <Body style={{ marginTop: '8px', fontWeight: 600 }}>
                {fieldInventoryText}
              </Body>
              <Small style={{ color: '#64748b' }}>{fieldTypeText}</Small>
            </div>
            <div
              style={{
                minWidth: '150px',
                padding: '12px 14px',
                borderRadius: '16px',
                border: `1px solid ${SURFACE_BORDER}`,
                background: '#fff',
              }}
            >
              <Small style={{ color: '#64748b', display: 'block' }}>
                Active page
              </Small>
              <Body style={{ marginTop: '8px', fontWeight: 600 }}>
                {pageStatusText}
              </Body>
            </div>
          </div>
        </div>

        <div
          style={{
            position: 'sticky',
            top: isTouchable ? '8px' : '12px',
            zIndex: 40,
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px',
            alignItems: 'stretch',
            padding: '14px',
            borderRadius: '20px',
            border: `1px solid ${SURFACE_BORDER}`,
            background: TOOLBAR_BACKGROUND,
            backdropFilter: 'blur(16px)',
            boxShadow: '0 12px 36px rgba(15, 23, 42, 0.1)',
          }}
        >
          <ToolbarGroup label="Navigation">
            <Button
              a11yLabel="Previous page"
              appearance="outline"
              label="Prev"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            />
            <Body style={{ minWidth: '110px', fontWeight: 600 }}>
              {numPages > 0 ? `Page ${currentPage} of ${numPages}` : 'Loading…'}
            </Body>
            <Button
              a11yLabel="Next page"
              appearance="outline"
              label="Next"
              disabled={currentPage >= numPages}
              onClick={() => setCurrentPage((p) => Math.min(numPages, p + 1))}
            />
            <input
              type="number"
              min={1}
              max={numPages || 1}
              placeholder="#"
              value={pageInput}
              onChange={(e) => setPageInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const page = parseInt(pageInput, 10);
                  if (!isNaN(page) && page >= 1 && page <= numPages) {
                    setCurrentPage(page);
                  }
                  setPageInput('');
                }
              }}
              style={{
                width: '60px',
                padding: '8px 10px',
                border: `1px solid ${SURFACE_BORDER}`,
                borderRadius: '10px',
                textAlign: 'center',
                fontSize: '14px',
                color: '#0f172a',
              }}
              aria-label="Go to page number"
            />
          </ToolbarGroup>

          <ToolbarGroup label="View">
            <Button
              a11yLabel="Zoom out"
              appearance="outline"
              label="-"
              disabled={(userScale ?? scale) <= 0.5}
              onClick={zoomOut}
            />
            <Small
              style={{
                minWidth: '48px',
                padding: '8px 10px',
                borderRadius: '10px',
                background: SURFACE_BACKGROUND,
                color: '#0f172a',
                fontWeight: 700,
                textAlign: 'center',
              }}
            >
              {Math.round((userScale ?? scale) * 100)}%
            </Small>
            <Button
              a11yLabel="Zoom in"
              appearance="outline"
              label="+"
              disabled={(userScale ?? scale) >= 2.0}
              onClick={zoomIn}
            />
            <Button
              a11yLabel="Fit to width"
              appearance="outline"
              label="Fit"
              onClick={zoomFit}
            />
          </ToolbarGroup>

          <ToolbarGroup label="Add fields">
            <Button
              a11yLabel="Add signature field"
              appearance="outline"
              label="Add Signature"
              onClick={() => addField('signature')}
            />
            <Button
              a11yLabel="Add date field"
              appearance="outline"
              label="Add Date"
              onClick={() => addField('date')}
            />
            <Button
              a11yLabel="Add name field"
              appearance="outline"
              label="Add Name"
              onClick={() => addField('name')}
            />
          </ToolbarGroup>

          <ToolbarGroup label="History">
            <Button
              a11yLabel="Undo"
              appearance="outline"
              label="Undo"
              disabled={historyIndex <= 0}
              onClick={undo}
            />
            <Button
              a11yLabel="Redo"
              appearance="outline"
              label="Redo"
              disabled={historyIndex >= history.length - 1}
              onClick={redo}
            />
          </ToolbarGroup>
        </div>

        <Small
          style={{
            color: '#64748b',
            paddingLeft: '4px',
          }}
        >
          Shortcuts: S/D/N add fields, Del remove, Ctrl+Z undo, Ctrl+Shift+Z
          redo, Ctrl+D duplicate, Esc deselect
        </Small>

        <div
          style={{
            border: `1px solid ${SURFACE_BORDER}`,
            borderRadius: '20px',
            display: 'flex',
            alignItems: 'stretch',
            overflow: 'hidden',
            background: SURFACE_BACKGROUND,
            minHeight: isTouchable ? '520px' : '640px',
            maxHeight: '74vh',
          }}
        >
          {!pdfLoading && !pdfError && (
            <PdfPageThumbnails
              pdfDoc={pdfDocRef.current}
              numPages={numPages}
              currentPage={currentPage}
              fieldsPerPage={fieldsPerPage}
              onPageSelect={setCurrentPage}
              collapsed={thumbnailsCollapsed}
              onToggleCollapse={() => setThumbnailsCollapsed((c) => !c)}
            />
          )}
          <div
            ref={viewerPaneRef}
            style={{
              flex: 1,
              overflow: 'auto',
              background:
                'radial-gradient(circle at top, #f8fbff 0%, #edf2f7 52%, #e2e8f0 100%)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              padding: isTouchable ? '16px' : '24px',
            }}
          >
            {pdfLoading ? (
              <div
                style={{
                  padding: '40px',
                  textAlign: 'center',
                  borderRadius: '18px',
                  border: `1px solid ${SURFACE_BORDER}`,
                  background: '#fff',
                  marginTop: '16px',
                  minWidth: '240px',
                }}
              >
                <Body>Loading PDF...</Body>
              </div>
            ) : pdfError ? (
              <div
                style={{
                  padding: '20px',
                  color: '#b91c1c',
                  borderRadius: '18px',
                  border: '1px solid #fecaca',
                  background: '#fff',
                  marginTop: '16px',
                }}
              >
                <Body>{pdfError}</Body>
              </div>
            ) : (
              <div
                ref={pageContainerRef}
                style={{
                  position: 'relative',
                  padding: '14px',
                  borderRadius: '22px',
                  border: `1px solid ${SURFACE_BORDER}`,
                  background: '#fff',
                  boxShadow: '0 20px 40px rgba(15, 23, 42, 0.12)',
                  cursor: dragging
                    ? 'grabbing'
                    : resizing
                      ? 'nwse-resize'
                      : 'default',
                  userSelect: 'none',
                }}
                onMouseDown={handlePointerDown}
                onMouseMove={handlePointerMove}
                onMouseUp={handlePointerUp}
                onMouseLeave={handlePointerUp}
                onTouchStart={handlePointerDown}
                onTouchMove={handlePointerMove}
                onTouchEnd={handlePointerUp}
                onTouchCancel={handlePointerUp}
              >
                <canvas ref={canvasRef} style={{ display: 'block' }} />

                <PdfAlignmentGuides
                  guides={activeGuides}
                  canvasWidth={pageWidth * scale}
                  canvasHeight={pageHeight * scale}
                />

                {fieldsOnCurrentPage.map((field) => {
                  const isSelected = field._id === selectedFieldId;
                  const config = getFieldConfig(field.field_type);
                  const { screenX, screenY, screenW, screenH } = pdfToScreen(
                    field.x,
                    field.y,
                    field.width,
                    field.height,
                  );

                  return (
                    <div
                      key={field._id}
                      style={{
                        position: 'absolute',
                        left: `${screenX}px`,
                        top: `${screenY}px`,
                        width: `${screenW}px`,
                        height: `${screenH}px`,
                        border: `2px ${isSelected ? 'solid' : 'dashed'} ${config.color}`,
                        borderRadius: '4px',
                        backgroundColor: config.bgColor,
                        cursor: dragging && isSelected ? 'grabbing' : 'grab',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'auto',
                        zIndex: isSelected ? 20 : 10,
                      }}
                    >
                      <span
                        style={{
                          position: 'absolute',
                          top: '10px',
                          left: '10px',
                          minWidth: '22px',
                          height: '22px',
                          padding: '0 6px',
                          borderRadius: '50%',
                          backgroundColor: config.color,
                          color: '#fff',
                          fontSize: '10px',
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: 30,
                        }}
                      >
                        {[...fields]
                          .sort((a, b) => a.sort_order - b.sort_order)
                          .findIndex((f) => f._id === field._id) + 1}
                      </span>
                      <span
                        style={{
                          fontSize: '11px',
                          color: config.color,
                          fontWeight: 600,
                          userSelect: 'none',
                        }}
                      >
                        {config.label}
                      </span>

                      {isSelected && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '-30px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            zIndex: 35,
                          }}
                        >
                          {/* Duplicate button */}
                          <div
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              duplicateField(field._id);
                            }}
                            style={{
                              width: '22px',
                              height: '22px',
                              borderRadius: '50%',
                              backgroundColor: config.color,
                              color: '#fff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '12px',
                              fontWeight: 700,
                              cursor: 'pointer',
                              boxShadow: '0 8px 16px rgba(15, 23, 42, 0.18)',
                              lineHeight: 1,
                            }}
                            title="Duplicate field"
                          >
                            +
                          </div>

                          {/* Delete button */}
                          <div
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              deleteField(field._id);
                            }}
                            style={{
                              width: '22px',
                              height: '22px',
                              borderRadius: '50%',
                              backgroundColor: config.color,
                              color: '#fff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '12px',
                              fontWeight: 700,
                              cursor: 'pointer',
                              boxShadow: '0 8px 16px rgba(15, 23, 42, 0.18)',
                              lineHeight: 1,
                            }}
                            title="Delete field"
                          >
                            {'\u00d7'}
                          </div>
                        </div>
                      )}

                      {/* Resize handles */}
                      {isSelected &&
                        (['tl', 'tr', 'bl', 'br'] as ResizeHandle[]).map(
                          (handle) => {
                            const isLeft = handle === 'tl' || handle === 'bl';
                            const isTop = handle === 'tl' || handle === 'tr';
                            return (
                              <div
                                key={handle}
                                style={{
                                  position: 'absolute',
                                  width: `${HANDLE_SIZE}px`,
                                  height: `${HANDLE_SIZE}px`,
                                  backgroundColor: config.color,
                                  border: '1px solid #fff',
                                  left: isLeft
                                    ? `${-HANDLE_SIZE / 2}px`
                                    : `${screenW - HANDLE_SIZE / 2}px`,
                                  top: isTop
                                    ? `${-HANDLE_SIZE / 2}px`
                                    : `${screenH - HANDLE_SIZE / 2}px`,
                                  cursor:
                                    handle === 'tl' || handle === 'br'
                                      ? 'nwse-resize'
                                      : 'nesw-resize',
                                  zIndex: 30,
                                }}
                              />
                            );
                          },
                        )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <PdfFieldSidebar
            fields={fields}
            selectedFieldId={selectedFieldId}
            onSelectField={handleSidebarSelectField}
            onReorder={handleSidebarReorder}
            collapsed={sidebarCollapsed}
            onToggleCollapse={() => setSidebarCollapsed((c) => !c)}
          />
        </div>

        <div style={{ marginTop: '4px' }}>
          <Small style={{ color: '#475569' }}>
            {fields.length === 0
              ? 'Use the buttons above to add signature, date, and name fields.'
              : summaryText}
          </Small>
        </div>
      </div>
    </div>
  );
};

export default PdfSignaturePlacer;
